import { supabase } from '../lib/supabase';
import { NewsArticle, NewsSource } from '../types/enhancedAnalysis';
import { manageNewsArticles, manageOfficialAnalysis } from '../lib/api';

export class NewsArticleService {
  /**
   * 根据分析ID获取关联的新闻文章
   */
  static async getNewsArticleByAnalysisId(analysisId: string): Promise<NewsArticle | null> {
    try {
      // 检查用户是否已登录
      const { data: { session } } = await supabase.auth.getSession();
      
      // 如果用户未登录，直接返回模拟数据，避免调用需要认证的API
      if (!session?.access_token) {
        console.log('User not logged in, returning mock news article for analysis:', analysisId);
        return this.getMockNewsArticle(analysisId);
      }

      // 首先获取分析记录，检查是否有关联的新闻文章
      const analysisResponse = await manageOfficialAnalysis('read', {
        id: analysisId,
        fields: ['article_id'],
        filters: { status: 1 }
      });

      if (!analysisResponse.success || !analysisResponse.data || !(analysisResponse.data as any).article_id) {
        console.warn('No news article found for analysis:', analysisId);
        // 返回模拟数据用于演示
        return this.getMockNewsArticle(analysisId);
      }

      // 然后获取新闻文章数据
      const newsResponse = await manageNewsArticles('read', {
        id: (analysisResponse.data as any).article_id,
        fields: [
          'id', 'title', 'summary', 'content', 'category',
          'publish_time', 'status', 'created_at', 'updated_at', 'original_url'
        ],
        include_relations: ['insight_news_source']
      });

      if (!newsResponse.success || !newsResponse.data) {
        console.warn('News article not found:', (analysisResponse.data as any).article_id);
        // 返回模拟数据用于演示
        return this.getMockNewsArticle(analysisId);
      }

      const newsData = newsResponse.data as any;
      const sourceData = newsData.insight_news_source;

      if (!sourceData) {
        console.warn('News source not found for article:', newsData.id);
        return this.getMockNewsArticle(analysisId);
      }

      return {
        id: newsData.id.toString(),
        title: newsData.title,
        summary: newsData.summary || '',
        content: newsData.content || '',
        source: {
          id: sourceData.id.toString(),
          name: sourceData.name,
          logo: undefined, // 字段暂未在数据库中实现
          credibilityScore: 0.8, // 默认值，后续可从数据库获取
          type: sourceData.type as 'official' | 'media' | 'blog' | 'social',
          website: undefined, // 字段暂未在数据库中实现
          description: undefined // 字段暂未在数据库中实现
        },
        publishTime: new Date(newsData.publish_time),
        category: newsData.category || '',
        tags: [], // 后续可扩展标签功能
        originalUrl: newsData.original_url, // 添加原文链接字段映射
        readingTime: Math.ceil((newsData.content?.length || 0) / 300),
        status: newsData.status,
        created_at: newsData.created_at,
        updated_at: newsData.updated_at
      };
    } catch (error) {
      console.error('Error fetching news article:', error);
      // 返回模拟数据用于演示
      return this.getMockNewsArticle(analysisId);
    }
  }

  /**
   * 获取模拟新闻数据用于演示
   */
  private static getMockNewsArticle(analysisId: string): NewsArticle {
    return {
      id: `mock-${analysisId}`,
      title: '宗馥莉还没有输，争产案决战杭州',
      summary: '娃哈哈创始人宗庆后的离世，不仅标志着一个传奇企业家时代的落幕，也将其一手创建的商业帝国推向了传承与纷争的十字路口。备受瞩目的"宗馥莉争产案"已从香港延烧至杭州，核心人物宗馥莉——这位被外界寄予厚望的"创二代"接班人，正面临其执掌娃哈哈以来最严峻的考验。',
      content: `这则新闻详细披露了宗馥莉与其三位同父异母手足之间，围绕高达21亿美元高净值信托的设立纠纷。这不仅是一场巨额财富的争夺战，更是一场关乎法律、道义、企业控制权和品牌声誉的复杂博弈。本文将以被告方宗馥莉的视角为核心，运用SWOT分析框架（优势、劣势、机会、威胁），对其在此次争产风暴中的战略处境进行深度剖析，以期揭示这场家门内斗背后的多重逻辑与未来走向。

从法律层面看，宗馥莉面临的挑战不容小觑。三位同父异母的手足声称，宗庆后在生前承诺为他们设立总值21亿美元的信托基金，但这一承诺并未得到兑现。他们指控宗馥莉作为遗产执行人，未能履行父亲的承诺，并要求法院强制执行。

然而，从宗馥莉的角度来看，这一指控缺乏充分的法律依据。首先，所谓的"承诺"是否具有法律约束力存在争议。在中国的法律体系下，口头承诺往往难以作为有效的法律证据，特别是在涉及如此巨额财产的情况下。其次，作为娃哈哈的实际控制人和主要继承人，宗馥莉对企业资产的处置权应当受到法律保护。

从商业角度分析，这场争产纠纷对娃哈哈品牌和业务运营的影响不可忽视。一方面，持续的法律纠纷可能会分散宗馥莉的注意力，影响其对企业的专注管理。另一方面，公众对于家族内部纠纷的关注，也可能对品牌形象造成负面影响。

但同时，这也为宗馥莉提供了一个展示其领导能力和企业治理水平的机会。如果她能够妥善处理这一危机，不仅能够巩固其在企业中的地位，还能够向外界证明其具备应对复杂局面的能力。`,
      source: {
        id: 'mock-source-1',
        name: '财经新闻网',
        logo: undefined,
        credibilityScore: 0.85,
        type: 'media',
        website: 'https://example.com',
        description: '专业财经媒体，专注企业分析和商业报道'
      },
      publishTime: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2天前
      category: '经济',
      tags: ['企业分析', '家族企业', '法律纠纷'],
      originalUrl: undefined, // 模拟数据不提供原文链接，避免误导用户
      readingTime: 14,
      status: 1,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  }

  /**
   * 获取与新闻文章相关的其他分析
   */
  static async getRelatedAnalysesByNewsId(newsId: string, excludeAnalysisId?: string): Promise<any[]> {
    try {
      // 检查用户是否已登录
      const { data: { session } } = await supabase.auth.getSession();
      
      // 如果用户未登录，返回空数组，避免调用需要认证的API
      if (!session?.access_token) {
        console.log('User not logged in, returning empty related analyses array');
        return [];
      }

      const filters: any = {
        article_id: newsId,
        status: 1
      };

      if (excludeAnalysisId) {
        filters.id_not = excludeAnalysisId;
      }

      const response = await manageOfficialAnalysis('list', {
        fields: ['id', 'title', 'summary', 'author_id', 'created_at', 'slug'],
        filters,
        limit: 5,
        order: { created_at: 'desc' }
      });

      if (!response.success) {
        console.error('Error fetching related analyses:', response.error);
        return [];
      }

      return (response.data as any) || [];
    } catch (error) {
      console.error('Error fetching related analyses:', error);
      return [];
    }
  }

  /**
   * 生成新闻分析关联信息
   */
  static generateNewsAnalysisRelation(newsArticle: NewsArticle, analysisTitle: string) {
    return {
      relevanceScore: 0.9, // 高相关性
      analysisAngle: `基于"${newsArticle.title}"的深度分析`,
      keyConnectionPoints: [
        '事件背景分析',
        '深层原因探讨',
        '影响评估',
        '未来趋势预测'
      ],
      backgroundContext: `本分析基于${newsArticle.source.name}发布的新闻报道，从多个维度深入解读事件的意义和影响。`,
      analysisType: 'deep_dive' as const
    };
  }

  /**
   * 格式化新闻摘要用于显示
   */
  static formatNewsSummary(summary: string, maxLength: number = 200): string {
    if (!summary) return '';
    
    if (summary.length <= maxLength) {
      return summary;
    }
    
    return summary.slice(0, maxLength).replace(/\s+\S*$/, '') + '...';
  }

  /**
   * 生成新闻标签
   */
  static generateNewsTags(newsArticle: NewsArticle): string[] {
    const tags: string[] = [];
    
    if (newsArticle.category) {
      tags.push(newsArticle.category);
    }
    
    tags.push(newsArticle.source.name);
    tags.push(newsArticle.source.type === 'official' ? '官方' : '媒体');
    
    // 基于发布时间添加时效性标签
    const daysSincePublish = Math.floor(
      (Date.now() - newsArticle.publishTime.getTime()) / (1000 * 60 * 60 * 24)
    );
    
    if (daysSincePublish === 0) {
      tags.push('今日');
    } else if (daysSincePublish <= 3) {
      tags.push('近期');
    } else if (daysSincePublish <= 7) {
      tags.push('本周');
    }
    
    return tags;
  }

  /**
   * 检查新闻内容质量
   */
  static validateNewsContent(newsArticle: NewsArticle): {
    isValid: boolean;
    issues: string[];
  } {
    const issues: string[] = [];
    
    if (!newsArticle.title || newsArticle.title.length < 10) {
      issues.push('新闻标题过短或缺失');
    }
    
    if (!newsArticle.summary || newsArticle.summary.length < 50) {
      issues.push('新闻摘要过短或缺失');
    }
    
    if (!newsArticle.content || newsArticle.content.length < 200) {
      issues.push('新闻内容过短或缺失');
    }
    
    if (!newsArticle.source.name) {
      issues.push('新闻来源信息缺失');
    }
    
    return {
      isValid: issues.length === 0,
      issues
    };
  }
}